import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import IconButton from '../../../shared/components/IconButton';
import useMediaLibrary from '../../hooks/useMediaLibrary';
import MediaLibraryPickerModal from './MediaLibraryPickerModal';

export default function PhotoReferenceImageField({
  graphicMediaId,
  graphicUrl,
  onChange,
}) {
  const fileInputRef = useRef(null);
  const [showPicker, setShowPicker] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  // enabled: false - this component needs fetchDownloadUrl and uploadFile, not the library listing,
  // and the hook fetches the whole list on mount otherwise.
  const { uploading, uploadFile, fetchDownloadUrl } = useMediaLibrary({ enabled: false });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setPreviewError(null);
      if (graphicMediaId) {
        try {
          const url = await fetchDownloadUrl(graphicMediaId);
          if (!cancelled) setPreviewUrl(url);
        } catch (err) {
          if (!cancelled) {
            setPreviewUrl(null);
            setPreviewError(err.message || 'Could not load preview');
          }
        }
        return;
      }

      if (graphicUrl) {
        if (!cancelled) setPreviewUrl(graphicUrl);
        return;
      }

      if (!cancelled) setPreviewUrl(null);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [graphicMediaId, graphicUrl, fetchDownloadUrl]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      const result = await uploadFile(file);
      if (result?.media?.id) {
        onChange({ graphic_media_id: result.media.id, graphic: '' });
      }
    } catch {
      // surfaced via hook
    }
  };

  const handleLibrarySelect = (item) => {
    onChange({ graphic_media_id: item.id, graphic: '' });
  };

  const handleRemove = () => {
    onChange({ graphic_media_id: null, graphic: '' });
  };

  return (
    <div className="mb-3">
      <label className="form-label form-label-caps">Reference image</label>
      <p className="small text-muted mb-2">
        Optional example photo shown to the field engineer before capture.
      </p>

      {previewUrl && (
        <div className="text-center mb-2">
          <img
            src={previewUrl}
            alt="Reference"
            style={{ maxWidth: '100%', maxHeight: 160, height: 'auto', borderRadius: 8 }}
          />
        </div>
      )}
      {previewError && (
        <div className="small text-danger mb-2">{previewError}</div>
      )}

      <div className="d-grid gap-2">
        <IconButton
          type="button"
          className="btn btn-outline-primary btn-sm"
          icon="bi-images"
          iconPosition="start"
          onClick={() => setShowPicker(true)}
        >
          Choose from library
        </IconButton>
        <IconButton
          type="button"
          className="btn btn-outline-primary btn-sm"
          icon="bi-upload"
          iconPosition="start"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? 'Uploading…' : 'Upload image'}
        </IconButton>
        {(graphicMediaId || graphicUrl) && (
          <IconButton
            type="button"
            className="btn btn-outline-secondary btn-sm"
            icon="bi-x-lg"
            iconPosition="start"
            onClick={handleRemove}
          >
            Remove image
          </IconButton>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="d-none"
        onChange={handleUpload}
      />

      <MediaLibraryPickerModal
        show={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={handleLibrarySelect}
        selectedId={graphicMediaId}
      />
    </div>
  );
}

PhotoReferenceImageField.propTypes = {
  graphicMediaId: PropTypes.number,
  graphicUrl: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

PhotoReferenceImageField.defaultProps = {
  graphicMediaId: null,
  graphicUrl: '',
};
